import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, Navigation, Clock, Gauge, MapPin, Wifi, WifiOff, Square, Activity, AlertTriangle, Zap } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useDateLocale } from '@/hooks/useDateLocale';
import { useTranslation } from 'react-i18next';
import type { Position, Device, Event } from '@/lib/traccar-api';

interface PositionHistoryBellProps {
  positionHistory: Position[];
  devices: Device[];
  events: Event[];
  onPositionClick?: (position: Position) => void;
}

const getEventIcon = (type: string) => {
  switch (type) {
    case 'deviceOnline':
      return <Wifi className="h-3.5 w-3.5 text-success" />;
    case 'deviceOffline':
      return <WifiOff className="h-3.5 w-3.5 text-destructive" />;
    case 'deviceMoving':
      return <Navigation className="h-3.5 w-3.5 text-primary" />;
    case 'deviceStopped':
      return <Square className="h-3.5 w-3.5 text-warning" />;
    case 'alarm':
      return <AlertTriangle className="h-3.5 w-3.5 text-destructive" />;
    case 'ignitionOn':
    case 'ignitionOff':
      return <Zap className="h-3.5 w-3.5 text-warning" />;
    default:
      return <Activity className="h-3.5 w-3.5 text-muted-foreground" />;
  }
};

export const PositionHistoryBell = ({ 
  positionHistory, 
  devices, 
  events,
  onPositionClick,
}: PositionHistoryBellProps) => {
  const dateFnsLocale = useDateLocale();
  const { t } = useTranslation();
  const getDeviceName = (deviceId: number) => {
    const device = devices.find(d => d.id === deviceId);
    return device?.name || `Device ${deviceId}`;
  };

  const getEventLabel = (type: string) => {
    const fallback = type.replace(/([A-Z])/g, ' $1').trim();
    return t(`notifications.events.${type}`, { defaultValue: fallback });
  };

  const formatSpeed = (speed: number) => {
    const kmh = speed * 1.852;
    return `${kmh.toFixed(1)} km/h`;
  };

  const eventCount = events.length;
  const positionCount = positionHistory.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-5 w-5" />
          {eventCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 flex items-center justify-center text-xs font-semibold"
            >
              {eventCount > 99 ? '99+' : eventCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Tabs defaultValue="events" className="w-full">
          <div className="p-3 border-b border-border">
            <TabsList className="w-full">
              <TabsTrigger value="events" className="flex-1">
                {t('nav.events')} {eventCount > 0 && `(${eventCount})`}
              </TabsTrigger>
              <TabsTrigger value="positions" className="flex-1">
                {t('route.totalPositions')} {positionCount > 0 && `(${positionCount})`}
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="events" className="m-0">
            <ScrollArea className="h-[300px]">
              <div className="p-2 space-y-1">
                {events.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('positionBell.noEvents')}
                  </p>
                ) : (
                  events.slice(0, 50).map((event, index) => (
                    <div
                      key={`${event.id}-${index}`}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors text-xs"
                    >
                      <div className="mt-0.5">
                        {getEventIcon(event.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {getDeviceName(event.deviceId)}
                        </p>
                        <p className="text-muted-foreground">
                          {getEventLabel(event.type)}
                        </p>
                      </div>
                      <span className="text-muted-foreground whitespace-nowrap flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(event.eventTime), { addSuffix: true, locale: dateFnsLocale })}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="positions" className="m-0">
            <ScrollArea className="h-[300px]">
              <div className="p-2 space-y-1">
                {positionHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('positionBell.noPositionUpdates')}
                  </p>
                ) : (
                  positionHistory.slice(0, 50).map((position, index) => (
                    <div
                      key={`${position.id}-${index}`}
                      onClick={() => onPositionClick?.(position)}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors text-xs cursor-pointer"
                    >
                      <div className="mt-0.5">
                        <Navigation 
                          className="h-3.5 w-3.5 text-primary" 
                          style={{ transform: `rotate(${position.course || 0}deg)` }}
                        />
                      </div>
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-foreground truncate min-w-0">
                            {getDeviceName(position.deviceId)}
                          </span>
                          <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                            <Gauge className="h-3 w-3" />
                            {formatSpeed(position.speed)}
                          </span>
                        </div>
                        <p className="text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span>{position.address || t('positionBell.addressUnavailable')}</span>
                        </p>
                      </div>
                      <span className="text-muted-foreground whitespace-nowrap flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(position.fixTime), 'HH:mm:ss')}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};
